import { Controller } from "@hotwired/stimulus"
import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom"

// Keeps the toolbar's bulk actions on one line: actions that do not fit move, from the last one backwards, into a
// "More" menu. A menu item clicks the original button, so confirmation and submission stay with the list controller.
export default class extends Controller {
  static targets = ["action", "more", "moreButton", "menu"]

  connect() {
    // The children change size when a selection shows the bulk actions or the counter grows a digit.
    this.observer = new ResizeObserver(() => this.layout())
    ;[this.element, ...this.element.children].forEach(element => this.observer.observe(element))
    this.toggled = event => this.menuToggled(event)
    this.menuTarget.addEventListener("toggle", this.toggled)
    this.layout()
  }

  disconnect() {
    this.observer.disconnect()
    this.menuTarget.removeEventListener("toggle", this.toggled)
    this.cleanupPositioning?.()
  }

  overflowing() {
    return this.element.scrollWidth > this.element.clientWidth
  }

  // Shows every action, then hides from the end until the toolbar fits with the More button in place.
  layout() {
    this.actionTargets.forEach(action => { action.style.display = "" })
    this.moreTarget.hidden = true
    if (!this.overflowing()) return this.fillMenu([])

    this.moreTarget.hidden = false
    const hidden = []
    for (let index = this.actionTargets.length - 1; index >= 0 && this.overflowing(); index--) {
      this.actionTargets[index].style.display = "none"
      hidden.unshift(this.actionTargets[index])
    }
    this.fillMenu(hidden)
  }

  fillMenu(actions) {
    if (actions.length === 0 && this.menuTarget.matches?.(":popover-open")) this.menuTarget.hidePopover()
    this.menuTarget.replaceChildren(...actions.map(action => this.menuItem(action)))
  }

  menuItem(action) {
    const item = document.createElement("button")
    item.type = "button"
    item.setAttribute("role", "menuitem")
    item.className = "block w-full rounded-md px-3 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-100 focus-visible:bg-gray-100 outline-none cursor-pointer"
    item.textContent = action.textContent.trim()
    item.addEventListener("click", () => {
      this.menuTarget.hidePopover()
      action.click()
    })
    return item
  }

  menuToggled(event) {
    const open = event.newState === "open"
    this.moreButtonTarget.setAttribute("aria-expanded", String(open))
    this.cleanupPositioning?.()
    this.cleanupPositioning = null
    if (!open) return

    this.cleanupPositioning = autoUpdate(this.moreButtonTarget, this.menuTarget, () => {
      computePosition(this.moreButtonTarget, this.menuTarget, {
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })]
      }).then(({ x, y }) => Object.assign(this.menuTarget.style, { left: `${x}px`, top: `${y}px` }))
    })
    this.menuTarget.querySelector("button")?.focus()
  }
}
